"use client";

/**
 * usePersistedState
 * Drop-in replacement for useState that persists the value in sessionStorage.
 *
 * - Survives F5 / Cmd+R page refresh (stays in same browser tab session)
 * - Clears when the tab is closed (intentional — each session is fresh)
 * - Namespaced by `key` so each call site is independent
 *
 * Usage (identical to useState):
 *   const [activeTab, setActiveTab] = usePersistedState('profile-tab', 'overview');
 */

import { useState, useEffect, useCallback, useRef } from "react";

export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Read initial value synchronously from sessionStorage (SSR-safe)
  const [state, setStateRaw] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = sessionStorage.getItem(`bb_ui_${key}`);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  // Keep a ref so the setter closure always has the latest state
  const stateRef = useRef(state);
  stateRef.current = state;

  // Persist whenever state changes
  useEffect(() => {
    try {
      sessionStorage.setItem(`bb_ui_${key}`, JSON.stringify(state));
    } catch { /* quota or private-mode — silently ignore */ }
  }, [key, state]);

  const setState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStateRaw((prev) => {
        const next = typeof value === "function"
          ? (value as (prev: T) => T)(prev)
          : value;
        return next;
      });
    },
    []
  );

  return [state, setState];
}
